import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { ApiError } from '../../lib/api/ApiError';
import { useReducedMotion } from '../../shared/hooks/useReducedMotion';
import { useReveal } from '../../shared/hooks/useReveal';
import { useScrollSpy } from '../../shared/hooks/useScrollSpy';
import { useSplitLayout } from '../../shared/hooks/useSplitLayout';
import { useWheelForwarding } from '../../shared/hooks/useWheelForwarding';
import { generateBackdrop } from '../../shared/placeholder/backdrop';
import { About } from './components/About';
import { Contact } from './components/Contact';
import { Experience } from './components/Experience';
import { Section } from './components/Section';
import { Sidebar } from './components/Sidebar';
import { Skills } from './components/Skills';
import { Work } from './components/Work';
import { fetchPublicSite } from './publicApi';
import { publicQueryKeys } from './queryKeys';

const SECTIONS = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'work', label: 'Work' },
  { id: 'skills', label: 'Skills' },
  { id: 'contact', label: 'Contact' },
] as const;

const SECTION_IDS = SECTIONS.map((section) => section.id);

export function PublicSitePage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: publicQueryKeys.site,
    queryFn: fetchPublicSite,
  });

  const isSplit = useSplitLayout();
  const reducedMotion = useReducedMotion();

  const paneRef = useRef<HTMLElement | null>(null);
  const sideRef = useRef<HTMLElement | null>(null);

  // The pane is held in STATE as well as in a ref.
  //
  // The observer root has to be computed during render, and reading `paneRef.current`
  // there is a React rule violation — during a concurrent render the ref may point at a
  // node from a different render pass. The ref stays for the effect-only consumers
  // (scrollspy, wheel forwarding), and this state is what render reads.
  const [pane, setPane] = useState<HTMLElement | null>(null);

  const activeSectionId = useScrollSpy({ sectionIds: SECTION_IDS, paneRef, isSplit });

  useWheelForwarding(sideRef, paneRef, isSplit);

  // THE line that decides whether reveals work.
  //
  // Above the split breakpoint the right pane scrolls and the document does not, so the
  // observer must be rooted at the pane. Rooted at the viewport it would see every
  // section as permanently intersecting and fire them all at once. Below the breakpoint
  // the document scrolls and the root must be null.
  const revealRoot = isSplit ? pane : null;

  useReveal(paneRef, { root: revealRoot, disabled: reducedMotion });

  const backdropSrc = useMemo(
    () =>
      data?.settings.backdrop?.url ??
      generateBackdrop({ seed: 'abolarin', reducedMotion }),
    [data?.settings.backdrop, reducedMotion],
  );

  if (isPending) {
    return <StatusScreen title="Loading" detail="Fetching the site." />;
  }

  if (isError) {
    const unavailable = error instanceof ApiError && error.isUnavailable;

    return (
      <StatusScreen
        title={unavailable ? 'Not configured yet' : 'Could not load the site'}
        detail={
          unavailable
            ? 'Site settings have not been seeded.'
            : 'The content service is unreachable. Try again shortly.'
        }
      />
    );
  }

  const { settings, projects, experience, skillGroups } = data;

  return (
    <>
      {/* Fixed backdrop over the left half. Sits behind everything.
          NOTE: position:fixed escapes overflow but NOT a transform/filter/will-change
          containing block. Do not put a transform on an ancestor of this. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 z-0 h-[72vh] overflow-hidden split:h-screen split:w-[50vw]"
      >
        <img
          src={backdropSrc}
          alt=""
          className="block size-full object-cover opacity-50 motion-safe:animate-kenburns"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgb(10_10_11/0.62),rgb(10_10_11/0.72)_55%,rgb(10_10_11/0.94)),linear-gradient(180deg,rgb(10_10_11/0.5),transparent_30%,rgb(10_10_11/0.7))]" />
        <div className="absolute inset-y-0 right-0 w-40 bg-[linear-gradient(90deg,transparent,var(--color-bg))]" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-[linear-gradient(180deg,transparent,var(--color-bg))] split:h-36" />
      </div>

      <a
        href="#main"
        className="fixed left-0 top-0 z-100 -translate-y-full bg-accent px-4 py-2.5 text-[13px] font-medium text-bg focus:translate-y-0"
      >
        Skip to content
      </a>

      {/*
        The split layout is a FIXED-HEIGHT FLEX ROW, not position:sticky.
        100dvh with overflow-hidden on the wrapper means the left column physically
        cannot move, so sticky — which needs the document to scroll and fails silently
        when it doesn't — is avoided entirely.
      */}
      <div className="relative z-1 mx-auto flex max-w-[1152px] flex-col items-start gap-0 px-6 split:h-dvh split:flex-row split:items-stretch split:gap-16 split:overflow-hidden split:px-12">
        <Sidebar
          settings={settings}
          sections={SECTIONS}
          activeSectionId={activeSectionId}
        />

        <main
          id="main"
          ref={(node) => {
            paneRef.current = node;
            setPane(node);
          }}
          className="w-full pb-20 split:h-full split:flex-1 split:overflow-y-auto split:scroll-smooth split:pb-25 split:pl-0 split:pr-6 split:pt-25 split:[overscroll-behavior:contain]"
        >
          <Section id="about" label="About">
            <About settings={settings} />
          </Section>

          <Section id="experience" label="Experience">
            <Experience entries={experience} resumeUrl={settings.resumeUrl} />
          </Section>

          <Section id="work" label="Work">
            <Work projects={projects} />
          </Section>

          <Section id="skills" label="Skills">
            <Skills groups={skillGroups} />
          </Section>

          <Section id="contact" label="Contact">
            <Contact settings={settings} />
          </Section>
        </main>
      </div>
    </>
  );
}

function StatusScreen({
  title,
  detail,
}: {
  readonly title: string;
  readonly detail: string;
}) {
  return (
    <main className="grid min-h-dvh place-items-center px-6">
      <div className="text-center">
        <h1 className="m-0 text-2xl font-bold tracking-name text-text">{title}</h1>
        <p className="mt-2 text-muted">{detail}</p>
      </div>
    </main>
  );
}
